import { isClientId } from '@/common/network/gql-types'
import { formatGqlUserMessage } from '@/common/errors/gql-error-utils'
import type { ObjectTypeModel, PropertyDefinitionModel } from '@/core/gql/gql-models-override'
import type { UpdateObjectTypeInput } from '@/generated/graphql'
import {
  corePropDefs,
  isCorePropDef,
  type ObjectTypesService,
} from '../services/ObjectTypesService'
import type { UpdatePropertyDefinitionInput } from '../gql/UpdatePropertyDefinitionInput'
import { type ITypeFormController, type LocalPropDef } from './TypeFormController'

interface EditTypeControllerProps {
  type: ObjectTypeModel
  closePanel: () => void
}

export class EditTypeController implements ITypeFormController {
  public initialCustomProps!: LocalPropDef[]
  public initialMetaProps!: LocalPropDef[]

  private typeId!: number
  private originalName!: string
  private snapshotProps!: PropertyDefinitionModel[]
  private closePanelFn!: () => void

  constructor(private readonly objectTypesService: ObjectTypesService) {}

  public init({ type, closePanel }: EditTypeControllerProps): void {
    this.typeId = type.id
    this.closePanelFn = closePanel
    const nonBuiltIn = type.propertyDefinitions.filter((p) => !isCorePropDef(p))
    this.snapshotProps = nonBuiltIn
    this.originalName = type.name
    this.initialCustomProps = []
    this.initialMetaProps = []
    for (const p of nonBuiltIn) {
      const local = { id: p.id, name: p.name, type: p.type }
      if (p.isMeta) this.initialMetaProps.push(local)
      else this.initialCustomProps.push(local)
    }
  }

  public onClose(): void {
    this.closePanelFn()
  }

  public async onSubmit(
    name: string,
    finalCustomProps: LocalPropDef[],
  ): Promise<string | undefined> {
    const snapshotCustomProps = this.snapshotProps.filter((p) => !p.isMeta)

    const propertiesToDelete = snapshotCustomProps
      .filter((orig) => !finalCustomProps.some((f) => f.id === orig.id))
      .map((orig) => orig.id)

    const propertyCreateInputs = finalCustomProps
      .filter((p) => isClientId(p.id))
      .map((p) => ({ name: p.name, type: p.type }))

    const propUpdates: UpdatePropertyDefinitionInput[] = finalCustomProps.flatMap((prop) => {
      if (isClientId(prop.id)) return []
      const orig = snapshotCustomProps.find((o) => o.id === prop.id)
      if (!orig || orig.name === prop.name) return []
      return [{ id: orig.id, name: prop.name }]
    })

    const hasTypeChanges =
      name !== this.originalName || propertiesToDelete.length > 0 || propertyCreateInputs.length > 0
    const typeInput: UpdateObjectTypeInput | undefined = hasTypeChanges
      ? {
          id: this.typeId,
          ...(name !== this.originalName && { name }),
          ...(propertiesToDelete.length && { propertiesToDelete }),
          ...(propertyCreateInputs.length && { propertyCreateInputs }),
        }
      : undefined

    const { error } = await this.objectTypesService.batchUpdateType({
      typeId: this.typeId,
      typeInput,
      propUpdates: propUpdates.length ? propUpdates : undefined,
    })

    if (error) return formatGqlUserMessage(error.message)
    this.closePanelFn()
  }
}
